import { apiRequest } from '../../shared/api/client';

/**
 * Supplier master — mirrors what the current product's supplier screen edits.
 *
 *   GET    /api/v1/suppliers[?includeInactive&warehouseId]   suppliers:read
 *   GET    /api/v1/suppliers/{id}                            suppliers:read
 *   POST   /api/v1/suppliers                                 suppliers:write
 *   PUT    /api/v1/suppliers/{id}                            suppliers:write
 *   DELETE /api/v1/suppliers/{id}                            suppliers:write
 *   GET    /api/v1/suppliers/{id}/warehouses                 suppliers:read
 *   PUT    /api/v1/suppliers/{id}/warehouses                 suppliers:write
 *
 * The warehouse endpoints are the current product's domain grid: a supplier is offered on a purchase
 * order only for the warehouses it is assigned to.
 *
 * No QuickBooks fields. The integration is a paid add-on, so that mapping belongs to the add-on.
 */

const COOKIE_INIT: RequestInit = { credentials: 'include' };

export interface SupplierDto {
  supplierId: string;
  supplierNumber: string;
  supplierName: string;
  /** One field, as legacy has it: splitting means guessing where a name divides. */
  contactName: string | null;
  email: string | null;
  phone: string | null;
  contactFax: string | null;
  addressLine1: string | null;
  city: string | null;
  stateProvince: string | null;
  postalCode: string | null;
  countryCode: string | null;
  paymentTermId: string | null;
  paymentTermCode: string | null;
  paymentTermName: string | null;
  /** A legacy column its screen does not show; carried for the migration, so not on ours either. */
  creditLimit: number | null;
  comments: string | null;
  /** The customer's own identifier, used by the inbound import API rather than typed. */
  externalSupplierId: string | null;
  isActive: boolean;
  createdAtUtc: string;
  warehouseCount: number;
}

export interface SupplierWarehouseDto {
  warehouseId: string;
  warehouseCode: string;
  displayName: string;
  isActive: boolean;
  assignedAtUtc: string;
}

/** Everything editable. Create adds supplierNumber; update cannot change it. */
export interface SupplierInput {
  supplierName: string;
  contactName: string | null;
  email: string | null;
  phone: string | null;
  contactFax: string | null;
  addressLine1: string | null;
  city: string | null;
  stateProvince: string | null;
  postalCode: string | null;
  paymentTermId: string | null;
  comments: string | null;
  isActive: boolean;
}

export async function listSuppliers(
  options: { includeInactive?: boolean; warehouseId?: string } = {},
  signal?: AbortSignal,
): Promise<SupplierDto[]> {
  const params = new URLSearchParams();
  if (options.includeInactive) params.set('includeInactive', 'true');
  if (options.warehouseId) params.set('warehouseId', options.warehouseId);
  const query = params.toString();

  const { data } = await apiRequest<SupplierDto[]>(`/v1/suppliers/${query ? `?${query}` : ''}`, {
    method: 'GET',
    signal,
    ...COOKIE_INIT,
  });
  return data;
}

export async function createSupplier(
  body: SupplierInput & { supplierNumber: string },
): Promise<SupplierDto> {
  const { data } = await apiRequest<SupplierDto>('/v1/suppliers/', {
    method: 'POST',
    body,
    ...COOKIE_INIT,
  });
  return data;
}

export async function updateSupplier(id: string, body: SupplierInput): Promise<SupplierDto> {
  const { data } = await apiRequest<SupplierDto>(`/v1/suppliers/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body,
    ...COOKIE_INIT,
  });
  return data;
}

export async function deleteSupplier(id: string): Promise<void> {
  await apiRequest<void>(`/v1/suppliers/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    ...COOKIE_INIT,
  });
}

export async function listSupplierWarehouses(
  id: string,
  signal?: AbortSignal,
): Promise<SupplierWarehouseDto[]> {
  const { data } = await apiRequest<SupplierWarehouseDto[]>(
    `/v1/suppliers/${encodeURIComponent(id)}/warehouses`,
    { method: 'GET', signal, ...COOKIE_INIT },
  );
  return data;
}

/** Whole-set: what is absent is unassigned, and an empty list detaches every warehouse. */
export async function setSupplierWarehouses(
  id: string,
  warehouseIds: string[],
): Promise<SupplierWarehouseDto[]> {
  const { data } = await apiRequest<SupplierWarehouseDto[]>(
    `/v1/suppliers/${encodeURIComponent(id)}/warehouses`,
    { method: 'PUT', body: { warehouseIds }, ...COOKIE_INIT },
  );
  return data;
}
